/**
 * useActiveConnectorsCount — count of currently satisfied capabilities for
 * the authenticated user.
 *
 * Backs the composer footer indicator ("N fonti connesse") that previously
 * hardcoded ``6``. The number is derived from the capability manifest so it
 * stays in sync with every OAuth callback (the BE pushes
 * ``capabilities_changed`` via WS → ``CapabilityProvider`` refetches the
 * manifest → this selector re-renders).
 *
 * White-label discipline: we count ``state === "satisfied"`` slots; the
 * capability *names* (cassetto_fiscale, sdi_invoicing, open_banking, etc.)
 * are vendor-neutral so the count never leaks a provider into the UI.
 *
 * Returns ``0`` while the manifest is still loading — the footer falls back
 * to the "Nessuna fonte" copy so we never flash a stale "6 fonti" hint.
 */

import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { useMemo } from "react";

import { useCapabilityManifest } from "./useCapabilityManifest.js";

export function useActiveConnectorsCount(): number {
  const query = useCapabilityManifest();
  return useMemo(() => {
    const caps = query.data?.capabilities;
    if (!caps) return 0;
    let n = 0;
    for (const diff of Object.values(caps)) {
      if (diff.state === "satisfied") n += 1;
    }
    return n;
  }, [query.data]);
}

/**
 * Localized singular/plural label for the connected-sources counter. Takes the
 * active ``i18n`` so it works from any caller (composer footer, section guide)
 * and follows the user's locale instead of hardcoded Italian.
 *   - 0  → "Nessuna fonte connessa"
 *   - 1  → "1 fonte connessa"
 *   - N  → "N fonti connesse"
 */
export function formatActiveConnectorsLabel(i18n: I18n, count: number): string {
  if (count <= 0) return i18n._(msg`Nessuna fonte connessa`);
  if (count === 1) return i18n._(msg`1 fonte connessa`);
  return i18n._(msg`${count} fonti connesse`);
}
